import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "react-query";
import {
  AlertCircle,
  Download,
  ExternalLink,
  ArrowLeft,
  X,
  MessageSquareIcon,
} from "lucide-react";
import { fetchPortfolioItem, Product } from "@/services/ApiOps";
import { getImageURL, getDocumentURL } from "@/services/FirebaseTools";
import rdecSymbol from "@logos/RDEC-SYMBOL.png";

const IndividualPPreviewer: React.FC = () => {
  window.scroll(0, 0);
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [showPdf, setShowPdf] = React.useState<boolean>(false);

  // Fetch product details
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useQuery<Product | null>({
    queryKey: ["product", productId],
    queryFn: async () => {
      if (!productId) throw new Error("Product ID is missing");

      const data = await fetchPortfolioItem(productId);
      if (Array.isArray(data) && data.length > 0) return data[0];
      throw new Error("Product not found");
    },
    retry: false,
    staleTime: 60 * 1000, // 1 minute stale time
  });

  // Fetch image URL
  const imageQuery = useQuery({
    queryKey: ["productImage", product?.imagem],
    queryFn: () => getImageURL(product!.imagem),
    enabled: !!product?.imagem,
    staleTime: Infinity,
  });

  // Fetch PDF URL
  const pdfQuery = useQuery({
    queryKey: ["productPdf", product?.pdf],
    queryFn: () => getDocumentURL(product?.pdf ?? ""),
    enabled: !!product?.pdf,
    staleTime: Infinity,
  });

  const handlePdfOpen = () => {
    if (pdfQuery.data) setShowPdf(true);
  };

  const handlePdfClose = () => setShowPdf(false);
  const handleContactUs = () => navigate("/contact-us");

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg bg-white py-20 shadow-lg">
        <img src={rdecSymbol} className="mb-4 h-12 w-12 animate-bounce"></img>
        <p className="text-gray-600">Carregando informações...</p>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
        <AlertCircle className="mb-4 h-12 w-12 text-red-500" />
        <h2 className="mb-2 text-2xl font-bold text-gray-800">
          Error Loading Product
        </h2>
        <p className="mb-6 text-gray-600">
          {(error as Error)?.message || "Product not found"}
        </p>
        <Link
          to="/portfolio#portfolio-previewer"
          className="flex items-center text-blue-600 transition-colors hover:text-blue-800"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Return to Portfolio
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-5xl rounded-lg bg-white p-6 shadow-lg md:p-8">
          <div className="p-6 md:p-8">
            {/* Navigation */}
            <div className="mb-6">
              <Link
                to="/portfolio#portfolio-previewer"
                className="inline-flex items-center text-blue-600 transition-colors hover:text-blue-800"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar ao Portfólio
              </Link>
            </div>

            {/* Product Header */}
            <div className="mb-8 border-b pb-6">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                {product.nome}
              </h1>
              <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                <span className="rounded-md bg-blue-100 px-2 py-1 text-blue-800">
                  {product.categoria}
                </span>
                {product.subcategoria && (
                  <span className="rounded-md bg-gray-100 px-2 py-1 text-gray-800">
                    {product.subcategoria}
                  </span>
                )}
              </div>
            </div>

            {/* Grid Layout */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {/* Left Column: Image & PDF Buttons */}
              <div className="md:col-span-1">
                {imageQuery.data ? (
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <img
                      src={imageQuery.data}
                      alt={product.nome}
                      className="h-auto w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-100 p-8">
                    <p className="text-center text-gray-500">
                      {imageQuery.isLoading
                        ? "Carregando Imagem..."
                        : "Imagem indisponível"}
                    </p>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    onClick={handleContactUs}
                    className="flex w-full items-center justify-center rounded-md bg-blue-600 p-3 text-white transition-colors hover:bg-blue-700"
                  >
                    <MessageSquareIcon className="mr-2 h-5 w-5" />
                    Entre em Contato
                  </button>
                </div>
              </div>

              {/* Right Column: Description & Details */}

              <div className="md:col-span-2">
                <div className="mb-6">
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Descrição do Produto
                  </h2>
                  <div className="prose max-w-none text-gray-700">
                    {product.descricao || "Nenhuma descrição foi fornecida."}
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Informações Adicionais
                  </h2>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Tipo de Produto
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {product.categoria}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Subcategoria
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {product.subcategoria || "N/A"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        ID do Produto
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {product.id_produto}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        ID da Categoria
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {product.id_categoria}
                      </dd>
                    </div>
                  </dl>

                  {pdfQuery.data && (
                    <div className="mt-6 flex gap-2">
                      <button
                        onClick={handlePdfOpen}
                        className="flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2 text-blue-600 transition-colors hover:bg-gray-50"
                        disabled={pdfQuery.isLoading}
                      >
                        <ExternalLink className="mr-2 h-5 w-5" />
                        {pdfQuery.isLoading
                          ? "Loading PDF..."
                          : "Ver Documentação"}
                      </button>
                      <a
                        href={pdfQuery.data}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center justify-center rounded-md bg-blue-600 px-5 py-2 text-white transition-colors hover:bg-blue-700"
                      >
                        <Download className="mr-2 h-5 w-5" />
                        Baixar PDF
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview Overlay */}
      <AnimatePresence>
        {showPdf && pdfQuery.data && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handlePdfClose}
          >
            <motion.div
              className="relative h-full max-h-[90%] w-full max-w-[90%] overflow-hidden rounded-lg bg-white"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button
                onClick={handlePdfClose}
                className="absolute right-2 top-2 text-gray-600 hover:text-gray-800"
              >
                <X className="h-5 w-5" />
              </button>

              <iframe
                src={pdfQuery.data}
                title="pdf-preview"
                className="h-full w-full"
              />

              <div className="absolute bottom-2 right-2">
                <a
                  href={pdfQuery.data}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Baixar PDF
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default IndividualPPreviewer;
